import { useState, useRef, useEffect, useCallback } from 'react';
import type { Message } from '@spi/shared';
import { UserRole } from '@spi/shared';
import { useAuth } from '../../../hooks/useAuth';
import { useDeleteMessage, useMarkRead } from '../../../api/messages';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import type { MessageContextType } from '@spi/shared';

interface MessageThreadProps {
  messages: Message[];
  isLoading: boolean;
  onPost: (body: string, files?: File[]) => Promise<void>;
  isPosting: boolean;
  title: string;
  subtitle?: string;
  placeholder?: string;
  allowAttachments?: boolean;
  collapsible?: boolean;
  contextType?: MessageContextType;
  contextId?: number;
}

export function MessageThread({
  messages,
  isLoading,
  onPost,
  isPosting,
  title,
  subtitle,
  placeholder = 'Type a message...',
  allowAttachments = true,
  collapsible = false,
  contextType,
  contextId,
}: MessageThreadProps) {
  const { user } = useAuth();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [collapsed, setCollapsed] = useState(collapsible);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const deleteMutation = useDeleteMessage();
  const markReadMutation = useMarkRead();

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!collapsed) scrollToBottom();
  }, [messages.length, collapsed, scrollToBottom]);

  // Mark as read when thread is opened/viewed with messages
  useEffect(() => {
    if (!collapsed && contextType && contextId && messages.length > 0) {
      markReadMutation.mutate({ contextType, contextId });
    }
    // Only fire when collapsed changes or messages appear for the first time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, contextType, contextId, messages.length > 0]);

  const handleSubmit = async () => {
    if (!body.trim() && files.length === 0) return;
    await onPost(body.trim(), files.length > 0 ? files : undefined);
    setBody('');
    setFiles([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isPosting) handleSubmit();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).slice(0, 5 - files.length);
      setFiles((prev) => [...prev, ...newFiles].slice(0, 5));
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDelete = async () => {
    if (deleteTarget == null) return;
    await deleteMutation.mutateAsync(deleteTarget);
    setDeleteTarget(null);
  };

  const isAdmin = user?.role === UserRole.ADMIN;

  if (collapsible) {
    return (
      <div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 text-sm font-medium text-spi-slate hover:text-spi-green transition-colors w-full"
        >
          <svg className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {title} ({messages.length})
        </button>
        {!collapsed && (
          <div className="mt-3">
            <ThreadContent
              messages={messages}
              isLoading={isLoading}
              body={body}
              setBody={setBody}
              files={files}
              handleFileChange={handleFileChange}
              removeFile={removeFile}
              handleKeyDown={handleKeyDown}
              handleSubmit={handleSubmit}
              isPosting={isPosting}
              placeholder={placeholder}
              allowAttachments={allowAttachments}
              fileRef={fileRef}
              listRef={listRef}
              userId={user?.id}
              isAdmin={isAdmin}
              setDeleteTarget={setDeleteTarget}
            />
          </div>
        )}
        <ConfirmDialog
          open={deleteTarget != null}
          title="Delete Message"
          message="Are you sure you want to delete this message?"
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-spi-charcoal flex items-center gap-2">
          <svg className="w-5 h-5 text-spi-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {title}
        </h3>
        {subtitle && <p className="text-xs text-spi-slate mt-1 ml-7">{subtitle}</p>}
      </div>
      <ThreadContent
        messages={messages}
        isLoading={isLoading}
        body={body}
        setBody={setBody}
        files={files}
        handleFileChange={handleFileChange}
        removeFile={removeFile}
        handleKeyDown={handleKeyDown}
        handleSubmit={handleSubmit}
        isPosting={isPosting}
        placeholder={placeholder}
        allowAttachments={allowAttachments}
        fileRef={fileRef}
        listRef={listRef}
        userId={user?.id}
        isAdmin={isAdmin}
        setDeleteTarget={setDeleteTarget}
      />
      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete Message"
        message="Are you sure you want to delete this message?"
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function ThreadContent({
  messages,
  isLoading,
  body,
  setBody,
  files,
  handleFileChange,
  removeFile,
  handleKeyDown,
  handleSubmit,
  isPosting,
  placeholder,
  allowAttachments,
  fileRef,
  listRef,
  userId,
  isAdmin,
  setDeleteTarget,
}: {
  messages: Message[];
  isLoading: boolean;
  body: string;
  setBody: (v: string) => void;
  files: File[];
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (i: number) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleSubmit: () => void;
  isPosting: boolean;
  placeholder: string;
  allowAttachments: boolean;
  fileRef: React.RefObject<HTMLInputElement>;
  listRef: React.RefObject<HTMLDivElement>;
  userId?: number;
  isAdmin: boolean;
  setDeleteTarget: (id: number | null) => void;
}) {
  const roleBadge = (role: string) => {
    if (role === 'donor') return 'bg-blue-100 text-blue-700';
    if (role === 'recipient') return 'bg-purple-100 text-purple-700';
    if (role === 'admin') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div>
      {/* Message list */}
      <div ref={listRef} className="max-h-96 overflow-y-auto space-y-3 mb-4">
        {isLoading && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-spi-green border-t-transparent" />
          </div>
        )}
        {!isLoading && messages.length === 0 && (
          <p className="text-sm text-spi-slate text-center py-4">No messages yet</p>
        )}
        {messages.map((msg) => {
          const isOwn = msg.senderId === userId;
          return (
            <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.isDeleted
                  ? 'bg-gray-100 text-gray-400 italic'
                  : isOwn
                    ? 'bg-spi-green/10 border border-spi-green/20'
                    : 'bg-spi-seed border border-spi-parchment'
              }`}>
                {!msg.isDeleted && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-spi-charcoal">{msg.senderName}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${roleBadge(msg.senderRole)}`}>
                      {msg.senderRole}
                    </span>
                    {msg.senderOrganization && (
                      <span className="text-[10px] text-spi-slate">{msg.senderOrganization}</span>
                    )}
                  </div>
                )}
                {msg.isDeleted ? (
                  <p className="text-sm">[Message deleted]</p>
                ) : (
                  <>
                    <p className="text-sm text-spi-charcoal whitespace-pre-wrap break-words">{msg.body}</p>
                    {msg.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {msg.attachments.map((att) =>
                          att.mimeType.startsWith('image/') ? (
                            <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer">
                              <img src={att.url} alt={att.originalName} className="h-20 rounded-lg object-cover border border-spi-parchment" />
                            </a>
                          ) : (
                            <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-spi-parchment text-xs text-spi-green hover:bg-spi-seed">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              {att.originalName}
                            </a>
                          ),
                        )}
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-spi-slate">{new Date(msg.createdAt + 'Z').toLocaleString()}</span>
                  {!msg.isDeleted && (isOwn || isAdmin) && (
                    <button
                      onClick={() => setDeleteTarget(msg.id)}
                      className="text-[10px] text-red-400 hover:text-red-600 ml-2"
                      title="Delete message"
                    >
                      delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input area */}
      <div className="border-t border-spi-parchment pt-3">
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-spi-seed text-xs text-spi-charcoal">
                {f.name}
                <button onClick={() => removeFile(i)} className="text-spi-slate hover:text-red-500">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            className="input flex-1 resize-none text-sm py-2 min-h-[38px] max-h-24"
            style={{ height: 'auto', overflow: 'hidden' }}
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 96) + 'px';
            }}
          />
          {allowAttachments && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="p-2 rounded-xl text-spi-slate hover:text-spi-green hover:bg-spi-seed transition-colors"
                title="Attach files"
                disabled={files.length >= 5}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
            </>
          )}
          <button
            onClick={handleSubmit}
            disabled={isPosting || (!body.trim() && files.length === 0)}
            className="p-2 rounded-xl bg-spi-green text-white hover:bg-spi-green-dark transition-colors disabled:opacity-50"
            title="Send"
          >
            {isPosting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
        <p className="text-[10px] text-spi-slate mt-1">Press Enter to send, Shift+Enter for new line</p>
      </div>
    </div>
  );
}
